import React, { useState } from 'react';
import { AdminPageLayout, StatsGrid } from '@/components/layout/AdminPageLayout';
import { StatCard, AlertCard } from '@/components/layout/AdminStatCard';
import { Card } from '@/components/ui/card';
import { CheckCircle, DollarSign, Clock, FileText } from 'lucide-react';

const WarrantyReportsPage = () => {
  const [selectedReport, setSelectedReport] = useState('');
  const [dateRange, setDateRange] = useState({ start: '', end: '' });

  return (
    <AdminPageLayout
      title="📊 Compliance Reports"
      subtitle="Generate Florida OIR reports and compliance documentation"
      actions={
        <button className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg font-semibold text-white">
          📧 Schedule Auto-Reports
        </button>
      }
    >
      <StatsGrid cols={4}>
        <StatCard label="Contract Delivery Rate" value="99.2%" icon={CheckCircle} variant="success" />
        <StatCard label="Reserve Balance" value="$2.4M" icon={DollarSign} />
        <StatCard label="Pending Deliveries" value="3" icon={Clock} variant="warning" />
        <StatCard label="Reports Generated" value="42" icon={FileText} />
      </StatsGrid>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="p-6">
          <h3 className="text-lg font-semibold mb-4">🚨 Compliance Alerts</h3>
          <div className="space-y-3">
            <div className="p-3 bg-green-900/20 border border-green-700 rounded-lg">
              <div className="flex justify-between items-center">
                <span className="text-green-100">Contract delivery compliance</span>
                <span className="text-green-400 font-semibold">✓ PASS</span>
              </div>
              <p className="text-green-300 text-sm mt-1">§634.414 - Above 95% threshold</p>
            </div>
            <div className="p-3 bg-green-900/20 border border-green-700 rounded-lg">
              <div className="flex justify-between items-center">
                <span className="text-green-100">Financial responsibility</span>
                <span className="text-green-400 font-semibold">✓ PASS</span>
              </div>
              <p className="text-green-300 text-sm mt-1">§634.406 - Reserve funds sufficient</p>
            </div>
            <div className="p-3 bg-yellow-900/20 border border-yellow-700 rounded-lg">
              <div className="flex justify-between items-center">
                <span className="text-yellow-100">OIR quarterly filing</span>
                <span className="text-yellow-400 font-semibold">DUE 7 DAYS</span>
              </div>
              <p className="text-yellow-300 text-sm mt-1">Next filing deadline approaching</p>
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <h3 className="text-lg font-semibold mb-4">📋 Quick Reports</h3>
          <div className="space-y-3">
            <button className="w-full p-3 text-left bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors">
              📊 Contract Delivery Summary
            </button>
            <button className="w-full p-3 text-left bg-green-600 hover:bg-green-700 rounded-lg transition-colors">
              💰 Financial Position Report
            </button>
            <button className="w-full p-3 text-left bg-purple-600 hover:bg-purple-700 rounded-lg transition-colors">
              🏢 OIR Quarterly Filing
            </button>
            <button className="w-full p-3 text-left bg-orange-600 hover:bg-orange-700 rounded-lg transition-colors">
              📈 Performance Analytics
            </button>
          </div>
        </Card>
      </div>

      <Card className="p-6">
        <h3 className="text-lg font-semibold mb-4">🎯 Custom Report Generator</h3>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Report Type
            </label>
            <select
              value={selectedReport}
              onChange={(e) => setSelectedReport(e.target.value)}
              className="w-full p-3 bg-slate-800 rounded border border-slate-700 focus:border-blue-500 focus:outline-none"
            >
              <option value="">Select report type</option>
              <option value="contract-delivery">Contract Delivery Analysis</option>
              <option value="financial-position">Financial Position Report</option>
              <option value="contractor-performance">Contractor Performance</option>
              <option value="equipment-registry">Equipment Registry Report</option>
              <option value="claims-analysis">Claims Analysis</option>
              <option value="oir-filing">OIR Regulatory Filing</option>
              <option value="compliance-audit">Compliance Audit</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Start Date
            </label>
            <input
              type="date"
              value={dateRange.start}
              onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
              className="w-full p-3 bg-slate-800 rounded border border-slate-700 focus:border-blue-500 focus:outline-none"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              End Date
            </label>
            <input
              type="date"
              value={dateRange.end}
              onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
              className="w-full p-3 bg-slate-800 rounded border border-slate-700 focus:border-blue-500 focus:outline-none"
            />
          </div>
        </div>
        <div className="flex space-x-3">
          <button
            disabled={!selectedReport}
            className={`px-6 py-3 rounded-lg font-semibold ${
              selectedReport
                ? 'bg-blue-600 hover:bg-blue-700'
                : 'bg-slate-700 cursor-not-allowed'
            }`}
          >
            📊 Generate Report
          </button>
          <button
            disabled={!selectedReport}
            className={`px-6 py-3 rounded-lg font-semibold ${
              selectedReport
                ? 'bg-green-600 hover:bg-green-700'
                : 'bg-slate-700 cursor-not-allowed'
            }`}
          >
            📧 Generate & Email
          </button>
        </div>
      </Card>

      <Card className="p-6">
        <h3 className="text-lg font-semibold mb-4">📋 Recent Reports</h3>
        <div className="overflow-x-auto -mx-6">
          <table className="w-full">
            <thead className="bg-slate-800">
              <tr>
                <th className="p-4 text-left text-slate-300 font-semibold">Report Name</th>
                <th className="p-4 text-left text-slate-300 font-semibold">Type</th>
                <th className="p-4 text-left text-slate-300 font-semibold">Generated</th>
                <th className="p-4 text-left text-slate-300 font-semibold">Period</th>
                <th className="p-4 text-left text-slate-300 font-semibold">Status</th>
                <th className="p-4 text-left text-slate-300 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              <ReportRow
                name="Q4 2024 OIR Compliance Filing"
                type="OIR Regulatory"
                generated="2024-01-15 09:30"
                period="Q4 2024"
                status="filed"
              />
              <ReportRow
                name="December Contract Delivery Analysis"
                type="Compliance"
                generated="2024-01-01 14:22"
                period="Dec 2024"
                status="completed"
              />
              <ReportRow
                name="Financial Position Summary"
                type="Financial"
                generated="2023-12-28 11:15"
                period="Dec 2024"
                status="completed"
              />
              <ReportRow
                name="Contractor Performance Review"
                type="Analytics"
                generated="2023-12-25 16:45"
                period="Q4 2024"
                status="completed"
              />
              <ReportRow
                name="Equipment Registry Audit"
                type="Audit"
                generated="2023-12-20 10:30"
                period="Dec 2024"
                status="pending"
              />
            </tbody>
          </table>
        </div>
      </Card>

      <Card className="p-6">
        <h3 className="text-lg font-semibold mb-4">⚙️ Automated Reporting</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h4 className="font-medium text-slate-200 mb-3">Scheduled Reports</h4>
            <div className="space-y-2">
              <div className="flex justify-between items-center p-3 bg-slate-800 rounded">
                <span className="text-slate-300">Weekly Contract Delivery</span>
                <span className="text-green-400 text-sm">✓ Active</span>
              </div>
              <div className="flex justify-between items-center p-3 bg-slate-800 rounded">
                <span className="text-slate-300">Monthly Financial Summary</span>
                <span className="text-green-400 text-sm">✓ Active</span>
              </div>
              <div className="flex justify-between items-center p-3 bg-slate-800 rounded">
                <span className="text-slate-300">Quarterly OIR Filing</span>
                <span className="text-yellow-400 text-sm">⏳ Pending</span>
              </div>
            </div>
          </div>
          <div>
            <h4 className="font-medium text-slate-200 mb-3">Alert Thresholds</h4>
            <div className="space-y-2">
              <div className="flex justify-between items-center p-3 bg-slate-800 rounded">
                <span className="text-slate-300">Contract Delivery &lt; 95%</span>
                <span className="text-red-400 text-sm">🚨 Alert</span>
              </div>
              <div className="flex justify-between items-center p-3 bg-slate-800 rounded">
                <span className="text-slate-300">Reserve Balance &lt; $2M</span>
                <span className="text-yellow-400 text-sm">⚠️ Warning</span>
              </div>
              <div className="flex justify-between items-center p-3 bg-slate-800 rounded">
                <span className="text-slate-300">Filing Deadline &lt; 14 days</span>
                <span className="text-blue-400 text-sm">ℹ️ Notice</span>
              </div>
            </div>
          </div>
        </div>
      </Card>
    </AdminPageLayout>
  );
};

const ReportRow = ({
  name,
  type,
  generated,
  period,
  status
}: {
  name: string;
  type: string;
  generated: string;
  period: string;
  status: 'completed' | 'pending' | 'filed' | 'error';
}) => {
  const statusColors = {
    completed: 'bg-green-900 text-green-400',
    pending: 'bg-yellow-900 text-yellow-400',
    filed: 'bg-blue-900 text-blue-400',
    error: 'bg-red-900 text-red-400'
  };

  const statusLabels = {
    completed: 'Completed',
    pending: 'Pending',
    filed: 'Filed',
    error: 'Error'
  };

  return (
    <tr className="border-b border-slate-800 hover:bg-slate-800/50">
      <td className="p-4">
        <span className="font-semibold text-slate-100">{name}</span>
      </td>
      <td className="p-4">
        <span className="px-2 py-1 bg-slate-700 text-slate-300 rounded text-sm">{type}</span>
      </td>
      <td className="p-4 text-slate-300">{generated}</td>
      <td className="p-4 text-slate-300">{period}</td>
      <td className="p-4">
        <span className={`px-2 py-1 rounded text-sm font-medium ${statusColors[status]}`}>
          {statusLabels[status]}
        </span>
      </td>
      <td className="p-4">
        <div className="flex space-x-2">
          <button className="text-blue-400 hover:text-blue-300 text-sm">Download</button>
          <button className="text-green-400 hover:text-green-300 text-sm">View</button>
          <button className="text-purple-400 hover:text-purple-300 text-sm">Resend</button>
        </div>
      </td>
    </tr>
  );
};

export default WarrantyReportsPage;
